import { type FC, useMemo, useEffect } from 'react';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { parseCookies } from 'nookies';
import { useQuery } from 'urql';

import config, { getFullLocale } from '@packages/config';
import { ErrorBoundary, HeadMeta, getLanguageFromParams, useI18n } from '@packages/shared';
import { createUrqlServerClient } from '@packages/gql';
import { useAbTesting } from '@packages/abtesting';
import { NAVIGATION_TEST_ID } from '@packages/modules/src/Header/useHasNewNavigation';

import { getTopLevelCategories } from '@packages/search/src/utils/getTopLevelCategories';
import { startChildLogger } from '@packages/search/src/utils/pages/startChildLogger';
import { extractRequiredData } from '@packages/search/src/utils/pages/extractRequiredData';
import { handleSpecialResponses } from '@packages/search/src/utils/pages/handleSpecialResponses';
import { hasValidationErrors } from '@packages/search/src/utils/pages/hasValidationErrors';
import { logGraphqlErrors } from '@packages/search/src/utils/pages/logGraphqlErrors';
import { decodeSearchQuery } from '@packages/search/src/queryEncoding';
import {
  getSharedServerSideGqlVariables,
  useSharedClientSideGqlVariables,
} from '@packages/search/src/utils/gqlVariables';
import { usePersonalizationCookie } from '@packages/search/src/hooks/usePersonalizationCookie';
import { useAbTestingCookiesFromEps } from '@packages/search/src/hooks/useAbTestingCookiesFromEps';
import {
  InspiringSearchPreview,
  SearchDebugPanel,
  SearchKimRating,
  SearchResultPageDefaultContent,
  SearchResultPageLoadingFallback,
  SearchResultPageZeroResults,
  SearchScrollTopButton,
  Tracking,
} from '@packages/search';
import { useRememberedPagedata } from '@packages/shared/src/hooks/useScrollRestoration';
import { getMainAudience, Survey, cmsFetcher } from '@packages/cms-components';
import type { CmsDataJsonApi } from '@packages/cms-components/interfaces';
import NewsletterSheet from '@packages/cms-components/src/components/Forms/NewsletterSheet/NewsletterSheet';
import { getEcState, useConfig } from '@packages/utilities';
import { SearchResultPageDataDocument } from '@packages/gql/generated/shopping/SearchResultPageDataDocument';
import { useOriginalSearchQuery } from '@packages/search/src/hooks/useOriginalSearchQuery';
import { useCleanPathname } from '@packages/search/src/hooks/useCleanPathname';
import { promotionBannerDataFetcher } from '@packages/cms-components/src/modules/PromotionBanner/utils/fetch/promotionBannerDataFetcher';

/* GraphQL */ `
  query SearchResultPageData(
    $params: SearchParametersInput!
    $query: String!
    $searchQuery: String!
    $locale: String!
  ) {
    search(params: $params, query: $query) {
      __typename
      ...SearchResultPageDefaultContentFragment
      ...SearchResultPageZeroResultsFragment
      ...UseAbTestingCookiesFromEpsFragment
      ...InspiringSearchPreviewFragment
      ...SearchPageTrackingFragment
      ...SearchHandleSpecialResponsesFragment
      ...SearchHandleSpecialResponsesSearchPageFragment
      ...SearchKimRatingFragment

      ... on SearchResultBase {
        id
      }
    }

    seoForSearch(searchQuery: $searchQuery, locale: $locale) {
      ...ProductListSeoFragment
      title
      description
      keywords
      robots
      canonicalUrl
    }
  }
`;

type SearchResultPageProps = Omit<
  InferGetServerSidePropsType<typeof getServerSideProps>,
  'fallback' | 'urqlState'
>;

/**
 * Page for displaying search results
 * */
export const SearchResultPage: FC<SearchResultPageProps> = ({
  topLevelCategories,
  bannerData,
  referrer,
}) => {
  const cleanPathname = useCleanPathname();
  const searchquery = useOriginalSearchQuery();
  const { locale } = useI18n();

  if (!searchquery) {
    throw new Error('No search query');
  }

  const variables = {
    ...useSharedClientSideGqlVariables(referrer),
    query: searchquery,

    // seo variables
    searchQuery: searchquery,
    locale,
  };

  const [{ data }] = useQuery({
    query: SearchResultPageDataDocument,
    variables,
    context: useMemo(() => ({ suspense: false }), []),
  });

  usePersonalizationCookie();
  useAbTestingCookiesFromEps(
    data && 'id' in data.search && data.search.id ? data.search : undefined,
  );
  useRememberedPagedata();

  const seoResponse = data?.seoForSearch;

  const { title, description, keywords, robots, canonicalUrl } = seoResponse ?? {};

  const {
    host: { domain },
  } = useConfig();

  // no canonicallink from SEO API response -> use current url without params as canonical
  const canonicallink = canonicalUrl || `https://www.${domain}${cleanPathname}`.toLowerCase();

  const seoInformationExists = !!seoResponse;

  const { setOutcome } = useAbTesting();

  useEffect(() => {
    setOutcome(NAVIGATION_TEST_ID, { SERP: 1 });
  }, [setOutcome]);

  return (
    <>
      {/* <Tracking /> needs to be first component in page render flow because of timing issues with glycerin tracking */}
      {data && <Tracking maskedResult={data.search} />}

      {seoInformationExists && (
        <HeadMeta
          meta={{
            title,
            ...(description ? { description } : {}),
            ...(keywords ? { keywords } : {}),
            robots,
          }}
          links={{ canonical: canonicallink }}
        />
      )}

      {!data && <SearchResultPageLoadingFallback />}

      {data?.search.__typename === 'EmptySearchResult' && (
        <SearchResultPageZeroResults maskedResult={data.search} />
      )}

      {/* eslint-disable-next-line no-underscore-dangle */}
      {data?.search.__typename === 'SuccessfulSearchResult' && (
        <SearchResultPageDefaultContent
          maskedData={data.search}
          topLevelCategories={topLevelCategories}
          maskedSeoResponse={seoResponse}
          bannerData={bannerData}
          referrer={referrer}
        />
      )}

      {data?.search.__typename === 'SuggestionPreviewSearchResult' && (
        <InspiringSearchPreview maskedData={data.search} />
      )}

      <SearchScrollTopButton />

      <SearchDebugPanel document={SearchResultPageDataDocument} data={data} variables={variables} />

      <ErrorBoundary fallback={<span data-testid="newslettersheet-error" />}>
        <NewsletterSheet />
      </ErrorBoundary>

      <ErrorBoundary>
        <Survey />
      </ErrorBoundary>

      {data && (
        <ErrorBoundary>
          <SearchKimRating
            maskedData={data.search}
            searchPageType="SERP"
            templateParams={{ x17: referrer ?? 'x' }}
          />
        </ErrorBoundary>
      )}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default SearchResultPage;

export const getServerSideProps = (async (context) => {
  const { childLogger, getElapsedMilliseconds } = startChildLogger('SearchResultPage', context);

  const { query, req } = context;
  const language = getLanguageFromParams(query, config);

  const searchquery = decodeSearchQuery(query);

  if (!searchquery) {
    throw new Error('invalid route match: required query param is missing');
  }

  const locale = getFullLocale(language);
  const cookies = parseCookies(context);
  const referrer = req.headers.referer;

  childLogger.trace(
    getElapsedMilliseconds(),
    'starting fetch from APIs (EPS + SEO + promo banner)',
  );

  const { ssrCache, ssrClient } = createUrqlServerClient();

  const variables = {
    ...getSharedServerSideGqlVariables(query, locale, req.headers, cookies, referrer),
    query: searchquery,

    // seo variables
    searchQuery: searchquery,
    locale,
  };

  const searchGqlQuery = ssrClient.query(SearchResultPageDataDocument, variables).toPromise();

  const [searchGqlQueryResult, promotionBannerPromiseResult, bannerDataPromise] =
    await Promise.allSettled([
      searchGqlQuery,
      promotionBannerDataFetcher(query, req.cookies, context, locale),
      cmsFetcher<CmsDataJsonApi>({
        bucketUrl: config.banner.apiEndpoints.bucket,
        locale,
        pathname: searchquery ?? '',
      }),
    ]);

  childLogger.trace(
    getElapsedMilliseconds(),
    'completed fetch from APIs (EPS + SEO + promo banner)',
  );

  logGraphqlErrors(searchGqlQueryResult, {
    childLogger,
    document: SearchResultPageDataDocument,
    variables,
  });

  if (hasValidationErrors(searchGqlQueryResult)) {
    return { notFound: true }; // Better would be to return a 400 status code, but Next.js does not support that easily. This way, at least it's not a 5xx code.
  }

  const searchGqlData = extractRequiredData(searchGqlQueryResult, {
    childLogger,
    document: SearchResultPageDataDocument,
    variables,
  });

  const { specialResponse } = handleSpecialResponses({
    maskedResult: searchGqlData.search,
    maskedSearchPageResult: searchGqlData.search,
    context,
    childLogger,
    getElapsedMilliseconds,
  });

  if (specialResponse) return specialResponse;

  if (promotionBannerPromiseResult.status === 'rejected') {
    childLogger.debug(
      { description: 'Promotion Banner Data Fetch (Searchquery-Page)', searchQuery: searchquery },
      'Could not fetch promo banner data',
    );
  }

  if (bannerDataPromise.status === 'rejected') {
    childLogger.debug(
      { description: 'Banner Data Fetch (Categories-Page)', searchquery },
      'Could not fetch banner data',
      bannerDataPromise.reason,
    );
  }

  const audience = req.cookies ? getMainAudience(getEcState(req.cookies)) : 'anonymous';
  const promotionBannerCacheKey = `promotionBannerData-${language}-${audience}`;
  childLogger.trace(getElapsedMilliseconds(), 'retrieving L1 categories');

  const topLevelCategories = getTopLevelCategories();

  childLogger.trace(getElapsedMilliseconds(), 'finished, returning props');

  return {
    props: {
      urqlState: ssrCache.extractData(),
      fallback: {
        ...(promotionBannerPromiseResult.status === 'fulfilled' && {
          [promotionBannerCacheKey]: promotionBannerPromiseResult.value,
        }),
      } as Record<string, any>,
      ...(bannerDataPromise.status === 'fulfilled' &&
        bannerDataPromise.value && {
          bannerData: bannerDataPromise.value,
        }),
      ...(topLevelCategories && { topLevelCategories }),
      ...(referrer && { referrer }),
    },
  };
}) satisfies GetServerSideProps;
